import { defineStore } from 'pinia';
import { onRequestDefaultError, requests, removeNulls } from '🔗/utils/requests';
import stores from '🔗/stores';
import moment from 'moment';

const useReleasesApi = defineStore('releases-api', {
    state: () => ({
        defaultCover: {
            resized: new URL(`🔗/assets/img/releases/resized.jpg`, import.meta.url).href,
            original: new URL(`🔗/assets/img/releases/original.jpg`, import.meta.url).href
        }
    }),
    getters: {
        canView: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            if (payload.release?.state === 'PUBLISHED') {
                if (!payload.release?.preorderDate || moment().diff(moment(payload.release.preorderDate)) >= 0) {
                    return true;
                } 
            }

            return !!payload.release?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canAdd: (state) => (payload) => stores.user().isTrackCurator ||
            stores.user().artistsIds.indexOf(payload?.artist?.id) !== -1,

        canEditLicense: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            return !!payload.release?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canEditCuratorSettings: (state) => (payload) => stores.user().isTrackCurator,
        canEditOwnerSettings: (state) => (payload) => {
            return !!payload.release?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canEdit: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            return payload.release?.state !== 'PUBLISHED' &&
                !!payload.release?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canRemove: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            return !!payload.release?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canEditState: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            return !!payload.release?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canEditContributor: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            return !!payload.release?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canEditContributorState: (state) => (payload) => stores.user().isTrackCurator ||
            stores.user().artistsIds.indexOf(payload?.artist?.id) !== -1,


        canAddTrack: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            // Проверяем статус
            if (payload.release?.state === 'PUBLISHED') {
                return false;
            }

            // Проверяем релиз
            if (!payload.release?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                )) {
                return false;
            }

            // Проверяем трек
            return !!payload.track?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canEditTracks: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            // Проверяем статус
            if (payload.release?.state === 'PUBLISHED') {
                return false;
            }

            // Проверяем релиз
            return !!payload.release?.contributors?.find(item => 
                (item.artistId || item.artist?.id) &&
                stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                item.role === 'PRIMARY' &&
                item.state === 'CONFIRMED'
            );
        },

        contributors: (state) => ({
            add: state.addContributor,
            remove: state.removeContributor,
            list: state.listContributor,
            update: state.updateContributor,
            changeState: state.changeContributorState
        }),
        tracks: (state) => ({
            add: state.addTrack,
            remove: state.removeTrack,
            setPosition: state.setTrackPosition,
            list: state.getTracks
        })
    },
    actions: {
        async list(params = {}) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: '/releases/list',
                    params,
                    // signal: requests.signal('release-list')
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async get(id) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/releases/${ id }`,
                    signal: requests.signal('release-get', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async updateCover(id, file) {
            const formData = new FormData();
            formData.append('cover', file);

            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/releases/${id}/cover/`,
                    formData,
                    contentType: 'multipart/form-data',
                    signal: requests.signal('release-cover', id)
                });

                if ([200, 201].indexOf(result.status) !== -1) {
                    return result.data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async add(data) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: '/releases/create',
                    data: removeNulls(data),
                    // signal: requests.signal('release-create')
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async update(id, data) {
            try {
                const result = await this.$http({
                    method: 'PUT',
                    url: `/releases/${ id }`,
                    data,
                    signal: requests.signal('release-update', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async getAvailableStates(id) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/releases/workflow/available/${ id }`,
                    signal: requests.signal('release-workflow-available-states', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async changeState(id, newState) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/releases/workflow/${ id }/${ newState }`,
                    signal: requests.signal('release-change-state', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async remove(id, with_tracks=false) {
            try {
                const result = await this.$http({
                    method: 'DELETE',
                    url: `/releases/${ id }?with_tracks=${with_tracks}`,
                    signal: requests.signal('release-remove', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },

        // Contributors
        async addContributor(data) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: '/releases/contributor/create',
                    data: removeNulls(data),
                    // signal: requests.signal('release-contributor-create')
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async updateContributor(id, data) {
            try {
                const result = await this.$http({
                    method: 'PUT',
                    url: `/releases/contributor/${ id }`,
                    data,
                    signal: requests.signal('release-contributor-update', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async removeContributor(id) {
            try {
                const result = await this.$http({
                    method: 'DELETE',
                    url: `/releases/contributor/${ id }`,
                    signal: requests.signal('release-contributor-remove', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async changeContributorState(id, newState) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/releases/contributor/workflow/${ id }/${ newState }`,
                    signal: requests.signal('release-contributor-change-state', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async listContributor(params = {}) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: '/releases/contributor/list',
                    params,
                    // signal: requests.signal('release-contributor-list')
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },

        // Tracks
        async addTrack({ releaseId, trackId }) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/releases/${releaseId}/track/${trackId}`,
                    signal: requests.signal('release-track-add', `${releaseId}-${trackId}`)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                // onRequestDefaultError(e);
            }
        },
        async getTracks(releaseId) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/releases/${releaseId}/tracks?sort=position&sort_dir=asc`,
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async setTrackPosition({ releaseId, trackId, position }) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/releases/${releaseId}/track/${trackId}/position/${position}`,
                    signal: requests.signal('release-track-position', `${releaseId}-${trackId}`)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                // onRequestDefaultError(e);
            }
        },
        async removeTrack({ releaseId, trackId }) {
            try {
                const result = await this.$http({
                    method: 'DELETE',
                    url: `/releases/${releaseId}/track/${trackId}`,
                    signal: requests.signal('release-track-remove', `${releaseId}-${trackId}`)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                // onRequestDefaultError(e);
            }
        },
    }
});

export default useReleasesApi;
